import { Trans, useTranslation } from 'react-i18next';

import { useLocale } from '@/shared/stores/accountStore';
import { Box, Form, FormGenerator } from '@/shared/ui';
import { FormFieldValues } from '@/shared/ui/FormGenerator';
import { Link } from '@/shared/ui/lib/Link';
import { Logo } from '@/shared/ui/Logo';
import { RouterLink } from '@/shared/ui/RouterLink';
import { CustomTypography } from '@/shared/ui/Typography';

import { createRegistrationSchema } from '../model/schemas';
import { useRegister } from '../model/useRegister';

import { StyledBox } from './styles';

export const RegisterForm = () => {
  const { t } = useTranslation(['common']);
  const locale = useLocale();

  const defaultValues = {
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    accept_pd: false,
    accept_ula: false,
  };

  const formScheme = [
    {
      name: 'email',
      label: t('common:inputs.email'),
      type: FormFieldValues.Email,
    },
    {
      name: 'password',
      label: t('common:inputs.password'),
      type: FormFieldValues.Password,
      inputProps: {
        placeholder: t('common:inputs.placeholders.password'),
      },
    },
    {
      name: 'confirmPassword',
      label: t('common:inputs.confirmPassword'),
      type: FormFieldValues.Password,
      inputProps: {
        placeholder: t('common:inputs.placeholders.confirmPassword'),
      },
      dependencies: ['confirmPassword', 'password'],
    },
    {
      name: 'phone',
      label: t('common:inputs.phoneNumber'),
      type: FormFieldValues.Phone,
    },
    {
      name: 'accept_pd',
      label: (
        <CustomTypography variant="body1">
          <Trans i18nKey="common:inputs.agreeWithPersonalDataCollection">
            I agree with
            <Link
              href="https://www.letsgochat.ru/agreement"
              target="_blank"
              rel="noopener noreferrer"
            >
              personal data collection
            </Link>
          </Trans>
        </CustomTypography>
      ),
      type: FormFieldValues.Checker,
    },
    {
      name: 'accept_ula',
      label: (
        <CustomTypography variant="body1">
          <Trans i18nKey="common:inputs.agreeWithTerms">
            I agree with
            <Link
              href="https://www.letsgochat.ru/terms_user"
              target="_blank"
              rel="noopener noreferrer"
            >
              personal data collection
            </Link>
          </Trans>
        </CustomTypography>
      ),
      type: FormFieldValues.Checker,
    },
  ];

  const mutation = useRegister();

  const onSubmit = (data: {
    email: string;
    password: string;
    phone: string;
    accept_pd: boolean;
    accept_ula: boolean;
  }) => {
    mutation.mutate({
      ...data,
      email: data.email,
      password: data.password,
      phone: data.phone.replace(/[^\d+]/g, ''),
      locale,
      account_type: 'individual',
      accept_pd: data.accept_pd,
      accept_ula: data.accept_ula,
    });
  };

  return (
    <StyledBox>
      <Logo />
      <CustomTypography variant="h5" sx={{ mb: 1.5, mt: 1, fontWeight: 600 }}>
        {t('auth:registration.title')}
      </CustomTypography>
      <Form
        schema={createRegistrationSchema(t)}
        onSubmit={onSubmit}
        defaultValues={defaultValues}
        mode="onTouched"
        submitBtnText={t('common:buttons.continue')}
        mutation={mutation}
        submitBtnProps={{
          fullWidth: false,
          sx: { width: '180px', margin: '0 auto' },
        }}
      >
        <FormGenerator formScheme={formScheme} />
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
          marginTop="4px"
          marginBottom="12px"
        >
          {/* <CustomTypography
            variant="body2"
            component="div"
            sx={{ textAlign: 'justify', whiteSpace: 'pre-line' }}
          >
            <Box
              component="span"
              sx={{ color: (theme) => theme.palette.custom.black[30] }}
            >
              {t('auth:registration.descriptionPassword.part1')}{' '}
            </Box>
            <Box
              component="span"
              sx={{ color: (theme) => theme.palette.custom.black }}
            >
              {t('auth:registration.descriptionPassword.part2')}{' '}
            </Box>
            <Box
              component="span"
              sx={{ color: (theme) => theme.palette.custom.black[30] }}
            >
              {t('auth:registration.descriptionPassword.part3')}{' '}
            </Box>
            <Box
              component="span"
              sx={{ color: (theme) => theme.palette.custom.black }}
            >
              {t('auth:registration.descriptionPassword.part4')}{' '}
            </Box>
          </CustomTypography> */}

          <CustomTypography
            variant="body1"
            sx={{ color: (theme) => theme.palette.custom.black[30], mt: 3 }}
          >
            {t('auth:registration.gotAnAccount')}{' '}
            <RouterLink to="/auth/login" underline="hover" variant="body2">
              {t('common:buttons.login')}
            </RouterLink>
          </CustomTypography>
        </Box>
      </Form>
    </StyledBox>
  );
};
